import AlertIcon from '@assets/icons/alert.svg';
import ChevronFilters from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { ActionSheetContent } from './action-sheet';
import { ActivityIndicator } from './activity-indicator';
import { Buffer } from 'buffer';
import { Button } from './button';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { gql } from '@apollo/client';
import {
  ItemDetail_TagPrintingConfigurationQuery,
  ItemDetail_TagPrintingConfigurationQueryVariables,
  VariantPackPrintTagConfiguration,
} from '@/types';
import {
  ItemListOption,
  ItemListOptionSection,
} from '@/contexts/item-context-types';
import { ItemMenuOperationsContent } from './orders/item-menu-operations-content';
import { ItemSubtitle } from './item-subtitle';
import { RecyclerList } from './recycler-list';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { SelectInput } from './select-input';
import { Text, TouchableOpacity, View } from 'react-native';
import { Type } from './toast';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

const MAX_VARIANT_PACK_OPTIONS_TO_DISPLAY = 4;
const TAG_PRINTING_OPTION_HEIGHT = 64;
const TAG_PRINTING_OPTION_MARGIN = 8;

export const TAG_PRINTING_CONFIG_QUERY = gql`
  query ItemDetail_TagPrintingConfiguration(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackUUIDs: [uuid!]!
  ) {
    getVariantPackPrintTagConfiguration(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      variant_pack_id_list: $variantPackUUIDs
    ) {
      printer_config {
        language
        print_markup_content
      }
      tag_attributes {
        key
        value
      }
      variant_pack_id
    }
  }
`;

interface Props {
  goBack: () => void;
  onClose: () => void;
  selectedItem: ItemListOption;
  selectPrinter: () => void;
}

export const TagPrintingSummary: React.FC<Props> = ({
  onClose,
  goBack,
  selectedItem,
  selectPrinter,
}) => {
  const [styles, theme] = useStyles();
  const {
    canRequestPermission,
    clearSelectedDevice,
    hasPermission,
    requestPermission,
    selectedDevice,
  } = useBluetoothContext();
  const { showErrorToast, showToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();

  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const variantPacks = useMemo(
    () => selectedItem.metadata?.skus || [],
    [selectedItem],
  );

  const [selectedVariantPackUUID, setSelectedVariantPackUUID] = useState<
    string | null
  >(null);

  const variantPackUUIDs = useMemo(
    () => variantPacks.map((sku) => sku.variantPackUUID),
    [variantPacks],
  );

  const sections = useMemo<ItemListOptionSection[]>(() => {
    return [
      {
        data: variantPacks.map((vp) => ({
          metadata: vp,
          title: vp.variantPackUUID,
          value: vp.variantPackUUID,
        })),
        key: null,
      },
    ];
  }, [variantPacks]);

  const { data, error, hasResponse, loading } = useWrappedQuery<
    ItemDetail_TagPrintingConfigurationQuery,
    ItemDetail_TagPrintingConfigurationQueryVariables
  >(TAG_PRINTING_CONFIG_QUERY, {
    variables: {
      restaurantUUID,
      supplierUUID,
      variantPackUUIDs,
    },
  });

  useEffect(() => {
    if (error) {
      showErrorToast();
      goBack();
    } else if (hasResponse && !selectedVariantPackUUID) {
      const firstVariantPackUUID =
        data?.getVariantPackPrintTagConfiguration?.[0]?.variant_pack_id;
      if (firstVariantPackUUID) {
        setSelectedVariantPackUUID(firstVariantPackUUID);
      } else {
        showErrorToast(i18n.t('no_print_configuration'));
        goBack();
      }
    }
  }, [
    data,
    error,
    goBack,
    hasResponse,
    selectedVariantPackUUID,
    showErrorToast,
  ]);

  useEffect(() => {
    if (!canRequestPermission && !hasPermission) {
      showErrorToast(i18n.t('bluetooth_permissions.denied'));
      goBack();
    }
    // only run on mount, prevent state updates in bluetooth context
    // incorrectly triggering this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const variantPackPrintConfigMap = useMemo(() => {
    return (data?.getVariantPackPrintTagConfiguration || []).reduce<{
      [variantPacKUUID: string]: VariantPackPrintTagConfiguration;
    }>((result, config) => {
      result[config.variant_pack_id] = config;
      return result;
    }, {});
  }, [data]);

  const selectedVariantPackPrintConfig = selectedVariantPackUUID
    ? variantPackPrintConfigMap[selectedVariantPackUUID]
    : null;
  const printContent =
    selectedVariantPackPrintConfig?.printer_config.print_markup_content;

  return (
    <ItemMenuOperationsContent
      centerTitle={!hasPermission}
      hideCloseButton={!hasPermission}
      onClose={onClose}
      operationName={
        hasPermission ? 'print_tag' : 'bluetooth_permissions.title'
      }
    >
      <View style={styles.base}>
        {hasPermission ? (
          loading ? (
            <ActivityIndicator />
          ) : (
            <>
              {selectedVariantPackPrintConfig ? (
                <View style={styles.attributeContainer}>
                  {selectedVariantPackPrintConfig.tag_attributes.map(
                    (attribute) => (
                      <View key={attribute.key} style={styles.attributeRow}>
                        <Text style={TYPOGRAPHY.listTitle}>
                          {i18n.t(attribute.key)}
                        </Text>
                        <Text style={TYPOGRAPHY.p}>{attribute.value}</Text>
                      </View>
                    ),
                  )}
                </View>
              ) : null}
              <View
                style={[
                  styles.skuList,
                  {
                    height:
                      (TAG_PRINTING_OPTION_HEIGHT +
                        TAG_PRINTING_OPTION_MARGIN) *
                      Math.min(
                        variantPacks.length,
                        MAX_VARIANT_PACK_OPTIONS_TO_DISPLAY,
                      ),
                  },
                ]}
              >
                <RecyclerList
                  alwaysBounceVertical={false}
                  estimatedItemSize={
                    TAG_PRINTING_OPTION_HEIGHT + TAG_PRINTING_OPTION_MARGIN
                  }
                  extraData={{ selectedVariantPackUUID }}
                  ItemSeparatorComponent={null}
                  renderItem={({ item }) => {
                    const vp = item.metadata!;
                    return (
                      <TouchableOpacity
                        key={vp.variantPackUUID}
                        onPress={() =>
                          setSelectedVariantPackUUID(vp.variantPackUUID)
                        }
                        style={[
                          styles.sku,
                          selectedVariantPackUUID === vp.variantPackUUID
                            ? styles.selectedBorder
                            : null,
                        ]}
                      >
                        <ItemSubtitle
                          currencyCode={vp.currencyCode}
                          displayString={vp.packDisplayString}
                          showInlineModifiers={false}
                          sku={vp.externalID}
                          unitCount={vp.unitCount}
                        />
                        {variantPackPrintConfigMap[
                          vp.variantPackUUID
                        ] ? null : (
                          <AlertIcon fill={theme.colors.inactiveFill} />
                        )}
                      </TouchableOpacity>
                    );
                  }}
                  sections={sections}
                  testID="tag-printing-variant-pack-list"
                />
              </View>
              <SelectInput
                inputContainerStyle={[styles.actionButton, styles.selectButton]}
                label={selectedDevice ? '' : i18n.t('select_printer')}
                onPress={selectPrinter}
                rightElement={
                  <ChevronFilters
                    fill={theme.colors.primary}
                    height={8}
                    width={14}
                  />
                }
                value={selectedDevice ? selectedDevice.name : ''}
              />
              <Button
                disabled={!printContent}
                onPress={async () => {
                  if (!printContent) {
                    return;
                  }
                  try {
                    const isConnected =
                      selectedDevice && (await selectedDevice.isConnected());
                    if (!isConnected || !selectedDevice) {
                      clearSelectedDevice();
                      showToast({
                        content: i18n.t('bluetooth_device.disconnected'),
                        type: Type.WARNING,
                      });
                      selectPrinter();
                      return;
                    }
                    const printBuffer =
                      Buffer.from(printContent).toString('base64');
                    await selectedDevice.write(printBuffer, 'base64');
                    onClose();
                  } catch (e) {
                    showErrorToast();
                    captureException(e);
                  }
                }}
                style={styles.actionButton}
                trackingData={{
                  name: 'item_details.print_tag_button',
                  properties: {
                    variantPackUUID: selectedVariantPackUUID,
                  },
                }}
              >
                {printContent
                  ? i18n.t('print')
                  : i18n.t('no_print_configuration')}
              </Button>
            </>
          )
        ) : (
          <ActionSheetContent
            actions={[i18n.t('turn_on'), i18n.t('cancel')]}
            loadingActionIndex={permissionsLoading ? 0 : undefined}
            message={i18n.t('bluetooth_permissions.message')}
            onAction={async (_, idx) => {
              setPermissionsLoading(true);
              try {
                if (idx === 0) {
                  const granted = await requestPermission();
                  if (granted) {
                    return;
                  }
                  showErrorToast(i18n.t('bluetooth_permissions.denied'));
                }
                goBack();
              } catch {
                showErrorToast();
              } finally {
                setPermissionsLoading(false);
              }
            }}
            primaryIndices={[0]}
          />
        )}
      </View>
    </ItemMenuOperationsContent>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    marginTop: 16,
  },
  attributeContainer: {
    gap: 4,
    marginBottom: 16,
  },
  attributeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  base: {
    flexDirection: 'column',
  },
  selectButton: {
    borderColor: theme.colors.stroke,
  },
  selectedBorder: {
    borderColor: theme.colors.accent,
  },
  sku: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderColor: theme.colors.transparent,
    borderRadius: 4,
    borderWidth: 2,
    flexDirection: 'row',
    gap: 16,
    height: TAG_PRINTING_OPTION_HEIGHT,
    marginBottom: TAG_PRINTING_OPTION_MARGIN,
    marginHorizontal: SCREEN_PADDING,
    padding: 16,
  },
  // Set negative margin to have scroll indicator display outside list
  skuList: {
    marginHorizontal: -SCREEN_PADDING,
  },
}));
